import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Card from "../Cards/Card";
import ComboHeader from "../ComboHeader/ComboHeader";
import NativeObject from "../NativeObject/NativeObject";
import { Box, Grid } from "@mui/material";
import ObjectLoader from "../ObjectLoader/ObjectLoader";
import ProgressBar from "../ProgressBar/ProgressBar";
import { useMediaQuery } from "react-responsive";
import { useSelections } from "../../context/Selections";
import Kpi from "../KPI/Kpi";

function OnePageCard({
	app,
	KPITitle,
	percentageId,
	valueId,
	goalValueId,
	distributorGraphId,
	treemapGraphId,
	fullView,
}) {
	const notFullHd = useMediaQuery({ query: "(max-width: 1366px)" });
	const [percentageKPI, setPercentage] = useState(null);
	const [loadingCard, setLoadingCard] = useState(true);
	const { selections } = useSelections();
	const [distributorTitle, treemapTitle] = ["Distribuidores", "Categorias"];
	const goalValue = Kpi({
		app,
		objectId: goalValueId,
		updatesWithSelection: true,
	});

	const getColorFromPercentage = (percentage) => {
		switch (true) {
			case percentage < 0.5:
				return "#E95248";
			case percentage < 0.9:
				return "#FDB949";
			default:
				return "#38BC9A";
		}
	};

	const fetchKPIValue = (objectId) => {
		if (app) {
			let id = objectId;
			setLoadingCard(true);
			setTimeout(() => {
				return fullView
					? app.visualization.get(id).then((res) => {
							const valueInfo =
								res.model.layout.qHyperCube
									?.qGrandTotalRow[0] ?? null;
							setPercentage({
								text: valueInfo?.qText,
								value: valueInfo?.qNum,
								color: getColorFromPercentage(valueInfo?.qNum),
							});
					  })
					: setLoadingCard(false);
			}, 1000);
		}
	};

	useEffect(() => {
		if (app) {
			fetchKPIValue(percentageId);
		}
	}, [selections, fullView]);

	useEffect(() => {
		if (percentageKPI) {
			setLoadingCard(false);
		}
	}, [percentageKPI]);

	return (
		<Card
			app={app}
			key={KPITitle}
			style={{
				width: "100%",
				minHeight: "140px",
				height: "92vh",
			}}
		>
			{loadingCard ? (
				<ObjectLoader
					icon="circle"
					width={"100%"}
					height={"100%"}
					className="circle-wrapper"
				/>
			) : (
				<Grid
					flexDirection={"column"}
					justifyContent={"flex-start"}
					alignItems={"flex-start"}
					className="one-page-card-wrapper"
					style={{
						width: "100%",
						height: "100%",
						display: "flex",
					}}
				>
					{/* TITLE */}
					<Grid
						item
						xs={12}
						flexDirection={"row"}
						justifyContent={"flex-start"}
						alignItems={"flex-start"}
						style={{
							width: "100%",
							maxHeight: "5%",
						}}
					>
						<ComboHeader
							reference={{
								title: {
									hard: KPITitle,
								},
							}}
						/>
					</Grid>

					{/* KPI */}
					<Grid
						item
						xs={12}
						flexDirection={"column"}
						justifyContent={"flex-start"}
						alignItems={"flex-start"}
						style={{
							width: "100%",
							marginBottom: "-64px",
						}}
					>
						<Grid
							className={`value-wrapper`}
							item
							xs={12}
							flexDirection={"column-reverse"}
							justifyContent={"flex-start"}
							alignItems={"flex-start"}
						>
							<Grid
								container
								flexDirection={"column"}
								justifyContent={"flex-start"}
								alignItems={"flex-start"}
								style={{
									width: "100%",
									minWidth: "324px",
									height: "30%",
									display: "flex",
								}}
							>
								{/* PROGRESS */}
								{fullView ? (
									percentageKPI && (
										<ProgressBar
											key={KPITitle}
											bgColor={percentageKPI.color}
											completed={
												percentageKPI.value * 100
											}
										/>
									)
								) : (
									<ProgressBar
										key={KPITitle}
										bgColor={"grey"}
										completed={0}
									/>
								)}
							</Grid>

							<Grid
								item
								flexDirection={"row"}
								justifyContent={"flex-start"}
								alignItems={"flex-end"}
								style={{
									width: "100%",
									height: "50%",
									display: "flex",
									marginBottom: "4px",
								}}
							>
								{/* MAIN VALUE */}
								<NativeObject
									app={app}
									key={valueId}
									reference={{
										id: valueId,
									}}
									style={{}}
								/>
							</Grid>
						</Grid>
						{fullView ? (
							<Grid
								container
								flexDirection={"row"}
								justifyContent={"flex-start"}
								alignItems={"flex-start"}
								style={{
									width: "100%",
								}}
							>
								{/* GOAL VALUE */}
								<Grid
									className={`goal-wrapper`}
									container
									flexDirection={"row"}
									justifyContent={"flex-start"}
									alignItems={"center"}
								>
									{/* VALOR EM CINZA */}
									<Box
										component={"span"}
										className="goal-value"
									>
										{goalValue}
									</Box>

									<Box
										component={"span"}
										className="goal-text"
									>
										Meta
									</Box>
								</Grid>

								{/* GOAL PERCENTAGE */}
								<Grid
									className={`percentage-wrapper`}
									container
									flexDirection={"row"}
									justifyContent={"flex-start"}
									alignItems={"center"}
								>
									<Box
										component={"span"}
										className="percentage-value"
										sx={{
											color: percentageKPI.color,
										}}
									>
										{percentageKPI.text}
									</Box>
									<Box
										component={"span"}
										className="percentage-text"
									>
										Ating.
									</Box>
								</Grid>
							</Grid>
						) : (
							<span className="filter-warning">
								Selecione apenas 1 Mês/Ano para análise com meta
							</span>
						)}
					</Grid>

					{/* DISTRIBUTORS GRAPH */}
					<Grid
						item
						xs={12}
						flexDirection={"column"}
						justifyContent={"flex-start"}
						alignItems={"flex-start"}
						className="dist-graph-wrapper"
					>
						<Grid
							item
							xs={12}
							style={{
								width: "100%",
								minWidth: "164px",
								display: "flex",
							}}
						>
							{/* DISTRIBUTORS TITLE */}
							<ComboHeader
								reference={{
									title: {
										hard: distributorTitle,
									},
								}}
								legends={
									fullView
										? [
												{
													type: "bar",
													iconColor: "#F98561",
													name: "Meta",
												},
												{
													type: "bar",
													iconColor: "#55B6EB",
													name: "Realizado",
												},
										  ]
										: [
												{
													type: "bar",
													iconColor: "#55B6EB",
													name: "Realizado",
												},
										  ]
								}
								scale={notFullHd ?? null}
							/>
						</Grid>

						{/* GRAPH */}
						<Grid
							item
							xs={12}
							style={{
								height: "80%",
								width: "100%",
								display: "flex",
							}}
						>
							<NativeObject
								app={app}
								key={distributorGraphId}
								reference={{ id: distributorGraphId }}
							/>
						</Grid>
					</Grid>

					{/* TREEMAP GRAPH */}
					<Grid
						item
						xs={12}
						flexDirection={"column"}
						justifyContent={"flex-start"}
						alignItems={"flex-start"}
						style={{
							width: "100%",
							marginTop: "16px",
						}}
					>
						<Grid
							item
							xs={12}
							style={{
								width: "100%",
								minWidth: "164px",
								display: "flex",
								marginBottom: "-16px",
								paddingBottom: "0",
							}}
						>
							{/* TREEMAP TITLE */}
							<ComboHeader
								reference={{
									title: {
										hard: treemapTitle,
									},
								}}
							/>
						</Grid>

						{/* GRAPH */}
						<Grid
							item
							xs={12}
							style={{
								height: "95%",
								width: "100%",
								display: "flex",
							}}
						>
							<NativeObject
								app={app}
								key={treemapGraphId}
								reference={{ id: treemapGraphId }}
							/>
						</Grid>
					</Grid>
				</Grid>
			)}
		</Card>
	);
}

OnePageCard.defaultProps = {
	app: null,
	KPITitle: "",
	percentageId: null,
	valueId: null,
	goalValueId: null,
	distributorGraphId: null,
	treemapGraphId: null,
	fullView: true,
};

OnePageCard.propTypes = {
	app: PropTypes.object,
	KPITitle: PropTypes.string,
	percentageId: PropTypes.string,
	valueId: PropTypes.string,
	goalValueId: PropTypes.string,
	distributorGraphId: PropTypes.string,
	treemapGraphId: PropTypes.string,
	fullView: PropTypes.bool,
};

export default OnePageCard;
